import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Config } from "../config";
import qs from "qs";

const baseUrl = Config.serviceUrls.aiAssistantBaseUrl;
const userBaseUrl = Config.serviceUrls.userBaseUrl;

// Manage System Instruction

export async function getSystemInformation() {
  const response = await axios.get(`${baseUrl}/AiAssistantSystemInstruction`);

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data.data;
}

export function useSystemInformation() {
  return useQuery(["/AiAssistantSystemInstruction"], () =>
    getSystemInformation(),
  );
}

export async function updateSystemInstruction(data) {
  const response = await axios.put(
    `${baseUrl}/AiAssistantSystemInstruction`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data?.message;
}

export function useUpdateSystemInstructionMutation() {
  const queryClient = useQueryClient();
  return useMutation((data) => updateSystemInstruction(data), {
    onSuccess: () =>
      queryClient.invalidateQueries("/SystemInstruction/updateInstruction"),
  });
}

// Manage Document

export async function getDocumentsList(pageNo, pageSize, searchTerm) {
  const response = await axios.get(`${baseUrl}/AiAssistantDocument/List`, {
    params: {
      pageNo,
      pageSize,
      searchTerm,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data,
    totalCount: response.data.pagination.totalItems,
  };
}

export function useGetDocumentsList(pageNo, pageSize, searchTerm) {
  return useQuery(
    ["/ManageDocuments/allDocuments", pageNo, pageSize, searchTerm],
    () => getDocumentsList(pageNo, pageSize, searchTerm),
  );
}

export const uploadDocuments = async (params) => {
  const response = await axios.post(`${baseUrl}/AiAssistantDocument`, params);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

export async function deleteDocument(documentId) {
  const response = await axios.delete(
    `${baseUrl}/AiAssistantDocument?Id=${documentId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

// Manage Prompts

export async function getPromptsList(pageNo, pageSize, searchTerm) {
  const response = await axios.get(`${baseUrl}/AiAssistantPrompt/List`, {
    params: {
      pageNo,
      pageSize,
      searchTerm,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data,
    totalCount: response.data.pagination.totalItems,
  };
}

export function useGetPromptsList(pageNo, pageSize, searchTerm) {
  return useQuery(
    ["/ManagePrompts/allPrompts", pageNo, pageSize, searchTerm],
    () => getPromptsList(pageNo, pageSize, searchTerm),
  );
}

export async function deletePrompt(promptId) {
  const response = await axios.delete(
    `${baseUrl}/AiAssistantPrompt?Id=${promptId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function addPrompt(data) {
  const response = await axios.post(`${baseUrl}/AiAssistantPrompt`, data);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export function useAddPrompt() {
  const queryClient = useQueryClient();
  return useMutation((data) => addPrompt(data), {
    onSuccess: () => queryClient.invalidateQueries("/Ai-assistant/addPrompt"),
  });
}

export async function updatePrompt(data) {
  const response = await axios.put(`${baseUrl}/AiAssistantPrompt`, data);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export function useUpdatePrompt() {
  const queryClient = useQueryClient();
  return useMutation((data) => updatePrompt(data), {
    onSuccess: () => queryClient.invalidateQueries("/Prompt/updatePrompt"),
  });
}

// Chat Bot

export async function getChatResponse({
  message,
  OwnerId,
  AiChatSessionId,
  FilePaths,
}) {
  const params = new URLSearchParams({
    message: message,
    OwnerId: OwnerId,
    AiChatSessionId: AiChatSessionId,
  });

  if (FilePaths && Array.isArray(FilePaths) && FilePaths.length > 0) {
    FilePaths.forEach((filePath) => {
      params.append("FilePaths", filePath);
    });
  }

  const response = await axios.get(
    `${baseUrl}/AiAssistant/ReplyWithContext?${params.toString()}`,
  );

  if (response.data.statusCode === 200 || response.data.statusCode === 429) {
    return response?.data;
  }

  throw new Error(response?.data?.message);
}

//Manage AI Setting

export const updateOwnerAiSettings = async ({
  IsAiSettingsEnabled,
  Tokens,
  OwnerId,
}) => {
  let response = await axios.patch(
    `${baseUrl}/AiAssistantSystemInstruction/OwnerAiSettings?IsAiSettingsEnabled=${IsAiSettingsEnabled}&Tokens=${Tokens}&OwnerId=${OwnerId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

// Chat History

export async function getChatHistory(pageNo, pageSize, OwnerId, searchTerm) {
  const response = await axios.get(`${baseUrl}/AiAssistant/ChatSessions`, {
    params: {
      pageNo,
      pageSize,
      OwnerId,
      searchTerm,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data,
    totalCount: response.data.pagination.totalItems,
  };
}

export function useGetChatHistory(pageNo, pageSize, OwnerId, searchTerm) {
  return useQuery(
    ["/ChatHistory/allChats", pageNo, pageSize, OwnerId, searchTerm],
    () => getChatHistory(pageNo, pageSize, OwnerId, searchTerm),
  );
}

export async function getSessionChat({ pageNo, pageSize, AiChatSessionId }) {
  const response = await axios.get(`${baseUrl}/AiAssistant/ChatMessages`, {
    params: {
      pageNo,
      pageSize,
      AiChatSessionId,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data,
    totalCount: response.data.pagination.totalItems,
  };
}

export async function updateSessionTitle(id, title) {
  const response = await axios.put(`${baseUrl}/AiAssistant/ChatSession/Title`, {
    id,
    title,
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function deleteSession(Id) {
  const response = await axios.delete(
    `${baseUrl}/AiAssistant/ChatSession?Id=${Id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

//Chat Agreement

export const acceptAiPolicyAgreement = async () => {
  let response = await axios.patch(`${userBaseUrl}/User/AcceptAiPrivacyPolicy`);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

//Rate the response
export const rateAiMessage = async (params) => {
  const response = await axios.post(
    `${baseUrl}/AiAssistantRate/ChatMessage/Rate`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

//show icon on header
export const updateAiToggleToHeader = async (ShowAiToggleInHeader) => {
  let response = await axios.patch(
    `${userBaseUrl}/User/UpdateAiToggleInHeaderStatus?IsRequestFromMobile=false&ShowAiToggleInHeader=${ShowAiToggleInHeader} `,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

//rating & reviews

export async function getRatingsAndReviews(
  pageNo,
  pageSize,
  searchTerm,
  isArchived,
) {
  const response = await axios.get(
    `${baseUrl}/AiAssistantRate/ChatMessageRatings?PageNo=${pageNo}&PageSize=${pageSize}&SearchTerm=${searchTerm}&IsArchived=${isArchived}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data,
    totalCount: response.data.pagination.totalItems,
  };
}

export function useGetRatingsAndReviews(
  pageNo,
  pageSize,
  searchTerm,
  isArchived,
) {
  return useQuery(
    [
      "/reviewAndRatings/allreviewAndRatingList",
      pageNo,
      pageSize,
      searchTerm,
      isArchived,
    ],
    () => getRatingsAndReviews(pageNo, pageSize, searchTerm, isArchived),
  );
}

//Seen ChatMessage rating
export const seenChatReponseRating = async (id) => {
  let response = await axios.patch(
    `${baseUrl}/AiAssistantRate/ChatMessageRating/Seen?id=${id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

//Comment added by superadmin
export async function commentAddedBySuperAdmin(data) {
  const response = await axios.put(
    `${baseUrl}/AiAssistantRate/ChatMessageRating/Comment`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

//Archive ChatMessage rating
export const archiveOrUnarchiveChatReponseRating = async (id, isArchived) => {
  let response = await axios.patch(
    `${baseUrl}/AiAssistantRate/ChatMessageRating/Archive?Id=${id}&IsArchived=${isArchived}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

//Chat History List
export async function getChatSessionList(
  pageNo,
  pageSize,
  searchTerm,
  isArchived,
) {
  const response = await axios.get(
    `${baseUrl}/AiAssistant/SuperAdmin/ChatSessions?PageNo=${pageNo}&PageSize=${pageSize}&SearchTerm=${searchTerm}&IsArchived=${isArchived}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data,
    totalCount: response.data.pagination.totalItems,
  };
}

export function useGetChatSessionList(
  pageNo,
  pageSize,
  searchTerm,
  isArchived,
) {
  return useQuery(
    [
      "/ChatHistory/allchatHistoryList",
      pageNo,
      pageSize,
      searchTerm,
      isArchived,
    ],
    () => getChatSessionList(pageNo, pageSize, searchTerm, isArchived),
  );
}

//Archive ChatMessage History
export const archiveOrUnarchiveChatHistory = async (id, isArchived) => {
  let response = await axios.patch(
    `${baseUrl}/AiAssistant/SuperAdmin/ChatSession/Archive?Id=${id}&IsArchived=${isArchived}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

//Seen ChatMessage history
export const seenChatHistory = async (id) => {
  let response = await axios.patch(
    `${baseUrl}/AiAssistant/SuperAdmin/ChatSession/Seen?id=${id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

//Reported Responses List
export async function getResponseReportedList(
  pageNo,
  pageSize,
  searchTerm,
  isArchived,
) {
  const response = await axios.get(
    `${baseUrl}/AiAssistantReport/SuperAdmin/List?PageNo=${pageNo}&PageSize=${pageSize}&SearchTerm=${searchTerm}&IsArchived=${isArchived}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data,
    totalCount: response.data.pagination.totalItems,
  };
}

export function useGetResponseReportedList(
  pageNo,
  pageSize,
  searchTerm,
  isArchived,
) {
  return useQuery(
    [
      "/ResponseReported/allReportedList",
      pageNo,
      pageSize,
      searchTerm,
      isArchived,
    ],
    () => getResponseReportedList(pageNo, pageSize, searchTerm, isArchived),
  );
}

export const archiveOrUnarchiveResponseReported = async (id, isArchived) => {
  let response = await axios.patch(
    `${baseUrl}/AiAssistantReport/SuperAdmin/Archive?Id=${id}&IsArchived=${isArchived}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

//Seen Reported Response history
export const seenResponseReported = async (id) => {
  let response = await axios.patch(
    `${baseUrl}/AiAssistantReport/SuperAdmin/Seen?id=${id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

//Comment added by superadmin responses reported
export async function reportViewCommentAddedBySuperAdmin(data) {
  const response = await axios.put(
    `${baseUrl}/AiAssistantReport/SuperAdmin/Comment`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

//Rate the response
export const reportAiMessage = async (params) => {
  const response = await axios.post(`${baseUrl}/AiAssistantReport`, params);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

/** Document module for Owner/Admin */

//Document List
export async function getProjectDocumentsList(
  pageNo,
  pageSize,
  searchTerm,
  OwnerId,
  sortBy,
  sortDescending,
  officeIds = [],
  designationIds = [],
  categoryIds = [],
  fileTypes = [],
) {
  const params = {
    pageNo,
    pageSize,
    searchTerm,
    OwnerId,
    sortBy,
    sortDescending,
  };

  if (officeIds.length > 0) {
    officeIds.forEach((id) => {
      if (!params.officeIds) {
        params.officeIds = [];
      }
      params.officeIds.push(id);
    });
  }

  if (designationIds.length > 0) {
    designationIds.forEach((id) => {
      if (!params.designationIds) {
        params.designationIds = [];
      }
      params.designationIds.push(id);
    });
  }

  if (categoryIds.length > 0) {
    categoryIds.forEach((id) => {
      if (!params.categoryIds) {
        params.categoryIds = [];
      }
      params.categoryIds.push(id);
    });
  }

  if (fileTypes.length > 0) {
    fileTypes.forEach((type) => {
      if (!params.fileTypes) {
        params.fileTypes = [];
      }
      params.fileTypes.push(type);
    });
  }

  const response = await axios.get(`${baseUrl}/AiAssistantDocument/ByOwner`, {
    params,
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: "repeat" });
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return {
    items: response.data.data,
    totalCount: response.data.pagination.totalItems,
  };
}

export function useGetProjectDocumentsList(
  pageNo,
  pageSize,
  searchTerm,
  OwnerId,
  sortBy,
  sortDescending,
  officeIds = [],
  designationIds = [],
  categoryIds = [],
  fileTypes = [],
  options = {},
) {
  return useQuery(
    [
      "/ManageDocuments/allDocuments",
      pageNo,
      pageSize,
      searchTerm,
      OwnerId,
      sortBy,
      sortDescending,
      officeIds,
      designationIds,
      categoryIds,
      fileTypes,
    ],
    () =>
      getProjectDocumentsList(
        pageNo,
        pageSize,
        searchTerm,
        OwnerId,
        sortBy,
        sortDescending,
        officeIds,
        designationIds,
        categoryIds,
        fileTypes,
      ),
    options,
  );
}

// Category List
export async function getCategoryList() {
  const response = await axios.get(`${baseUrl}/AiAssistantDocument/Categories`);

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data.data;
}

export async function viewDocumentDetail(id) {
  const response = await axios.get(`${baseUrl}/AiAssistantDocument/${id}`);

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data.data;
}

export async function updateDocument(params) {
  const response = await axios.put(`${baseUrl}/AiAssistantDocument`, params);

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export const exportChat = async (params) => {
  const response = await axios.post(
    `${baseUrl}/AiAssistant/ChatMessages/Export`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};
